import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/shared/empty-state";
import { TableSkeleton } from "@/components/shared/loading-skeleton";
import { StatusBadge } from "@/components/shared/status-badge";
import { postApiProviderIdentityLinks } from "@/lib/api/generated/capabilities/capabilities";
import type { User } from "@/lib/api/generated/control-api.schemas";
import type {
  JsonObject,
  JsonValue,
  ProviderIdentityLinkRecord,
} from "../../../../../packages/shared-types/src/index.js";
import { queryKeys } from "@/lib/query/keys";
import type { GroupedIdentityMappingUser } from "./identity-view-model.js";

type ProviderLinkFormState = {
  userId: string;
  userId: string;
  providerKey: string;
  providerUserRef: string;
  delegatedAuthState: ProviderIdentityLinkRecord["delegatedAuthState"];
  lastVerifiedAt: string;
  metadata: string;
};

const EMPTY_JSON = "{}";

export function ProviderIdentityLinksSection({
  tenantId,
  users,
  mappedUsers,
  providerLinks,
  providerOptions,
  isLoading,
}: {
  tenantId: string;
  users: User[];
  mappedUsers: GroupedIdentityMappingUser[];
  providerLinks: ProviderIdentityLinkRecord[];
  providerOptions: string[];
  isLoading: boolean;
}) {
  const queryClient = useQueryClient();
  const saveProviderLink = useMutation({
    mutationFn: (record: ProviderIdentityLinkRecord) =>
      postApiProviderIdentityLinks({ record: record as Record<string, unknown> }),
    onSuccess: (_data, input) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.providerIdentityLinks(input.tenantId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.state });
    },
  });
  const usersById = new Map(users.map((user) => [user.id, user]));
  const [linkForm, setLinkForm] = useState<ProviderLinkFormState>({
    userId: "",
    providerKey: "",
    providerUserRef: "",
    delegatedAuthState: "ready",
    lastVerifiedAt: "",
    metadata: EMPTY_JSON,
  });

  async function handleSaveProviderLink() {
    try {
      const userId = linkForm.userId.trim();
      const providerKey = linkForm.providerKey.trim();
      const providerUserRef = linkForm.providerUserRef.trim();
      if (!userId || !providerKey || !providerUserRef) {
        throw new Error(
          "User, mapped Agentiq user, provider, and provider user ref are required.",
        );
      }

      const autoVerifiedAt =
        linkForm.delegatedAuthState === "ready" && !linkForm.lastVerifiedAt.trim()
          ? new Date().toISOString()
          : undefined;
      const metadata = parseOptionalJson(linkForm.metadata, "Provider link metadata");
      const record: ProviderIdentityLinkRecord = {
        tenantId,
        userId,
        providerKey,
        providerUserRef,
        delegatedAuthState: linkForm.delegatedAuthState,
        ...(linkForm.lastVerifiedAt.trim() || autoVerifiedAt
          ? { lastVerifiedAt: linkForm.lastVerifiedAt.trim() || autoVerifiedAt }
          : {}),
        ...(metadata ? { metadata } : {}),
      };
      await saveProviderLink.mutateAsync(record);
      setLinkForm((current) => ({
        ...current,
        userId: record.userId,
        providerKey: record.providerKey,
        providerUserRef: record.providerUserRef,
        lastVerifiedAt: record.lastVerifiedAt ?? "",
      }));
      const user = usersById.get(record.userId);
      toast.success(
        `Saved provider link for ${user?.displayName ?? record.userId}`,
      );
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to save provider link"));
    }
  }

  return (
    <Card>
      <CardHeader className="gap-2">
        <CardTitle>Provider Identity Links</CardTitle>
        <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
          Delegated and impersonation activations are anchored to assigned users and bridged to local Agentiq mappings for runtime execution.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="grid gap-3 md:grid-cols-2">
          <Field
            label="Assigned user"
            hint="This is the Agentiq user record for the link."
          >
            <select
              className="form-input"
              value={linkForm.userId}
              onChange={(event) =>
                setLinkForm((current) => ({
                  ...current,
                  userId: event.target.value,
                }))
              }
            >
              <option value="">Select user</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {formatUserOption(user)}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Agentiq mapped user"
            hint="This remains the runtime bridge until delegated execution resolves users directly."
          >
            <select
              className="form-input"
              value={linkForm.userId}
              onChange={(event) =>
                setLinkForm((current) => ({
                  ...current,
                  userId: event.target.value,
                }))
              }
            >
              <option value="">Select mapped user</option>
              {mappedUsers.map((user) => (
                <option key={user.userId} value={user.userId}>
                  {user.displayName} · {user.userId}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Provider"
            hint={
              providerOptions.length > 0
                ? "Only providers from delegated or impersonation activations are shown."
                : "Switch an activation to delegated user or admin impersonation to wire a provider link."
            }
          >
            <select
              className="form-input"
              value={linkForm.providerKey}
              onChange={(event) =>
                setLinkForm((current) => ({
                  ...current,
                  providerKey: event.target.value,
                }))
              }
            >
              <option value="">Select provider</option>
              {providerOptions.map((providerKey) => (
                <option key={providerKey} value={providerKey}>
                  {providerKey}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Provider user ref">
            <input
              className="form-input"
              value={linkForm.providerUserRef}
              onChange={(event) =>
                setLinkForm((current) => ({
                  ...current,
                  providerUserRef: event.target.value,
                }))
              }
            />
          </Field>
          <Field label="Delegated auth state">
            <select
              className="form-input"
              value={linkForm.delegatedAuthState}
              onChange={(event) =>
                setLinkForm((current) => ({
                  ...current,
                  delegatedAuthState:
                    event.target.value as ProviderIdentityLinkRecord["delegatedAuthState"],
                }))
              }
            >
              <option value="ready">ready</option>
              <option value="pending">pending</option>
              <option value="expired">expired</option>
              <option value="missing">missing</option>
            </select>
          </Field>
          <Field label="Last verified at">
            <input
              className="form-input font-mono text-xs"
              placeholder="2026-03-23T12:00:00.000Z"
              value={linkForm.lastVerifiedAt}
              onChange={(event) =>
                setLinkForm((current) => ({
                  ...current,
                  lastVerifiedAt: event.target.value,
                }))
              }
            />
          </Field>
        </div>
        <Field label="Metadata">
          <textarea
            className="form-input min-h-28 font-mono text-xs"
            value={linkForm.metadata}
            onChange={(event) =>
              setLinkForm((current) => ({
                ...current,
                metadata: event.target.value,
              }))
            }
          />
        </Field>
        <div className="flex justify-end">
          <Button onClick={handleSaveProviderLink} disabled={saveProviderLink.isPending || !tenantId}>
            {saveProviderLink.isPending ? "Saving..." : "Save provider link"}
          </Button>
        </div>

        {isLoading ? (
          <TableSkeleton rows={3} cols={4} />
        ) : providerLinks.length === 0 ? (
          <EmptyState
            title="No provider links yet"
            description="Save a provider identity link before delegated activations can execute for an assigned user."
          />
        ) : (
          <div className="grid gap-3">
            {providerLinks.map((record) => (
              <button
                key={`${record.providerKey}:${record.userId}`}
                type="button"
                className="rounded-md border px-3 py-3 text-left transition-colors hover:border-(--border-hover) hover:bg-(--bg-hover)"
                style={{ borderColor: "var(--border)" }}
                onClick={() =>
                  setLinkForm({
                    userId: record.userId,
                    providerKey: record.providerKey,
                    providerUserRef: record.providerUserRef,
                    delegatedAuthState: record.delegatedAuthState,
                    lastVerifiedAt: record.lastVerifiedAt ?? "",
                    metadata: stringifyJson(record.metadata),
                  })
                }
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium" style={{ color: "var(--text-strong)" }}>
                      {formatProviderLinkTitle(record, usersById)}
                    </div>
                    <div
                      className="mt-1 text-xs font-mono"
                      style={{ color: "var(--muted-foreground)" }}
                    >
                      {record.providerKey} · {record.providerUserRef}
                    </div>
                    <div
                      className="mt-1 text-[11px]"
                      style={{ color: "var(--muted-foreground)" }}
                    >
                      Agentiq mapping: {record.userId}
                    </div>
                  </div>
                  <StatusBadge status={record.delegatedAuthState} />
                </div>
                <div className="mt-2 text-[11px]" style={{ color: "var(--muted-foreground)" }}>
                  {record.lastVerifiedAt
                    ? `Verified ${record.lastVerifiedAt}`
                    : "Verification has not been recorded yet."}
                </div>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>
        {label}
      </label>
      {children}
      {hint ? (
        <div className="text-[11px]" style={{ color: "var(--muted-foreground)" }}>
          {hint}
        </div>
      ) : null}
    </div>
  );
}

function parseOptionalJson(value: string, label: string): JsonObject | undefined {
  if (!value.trim()) {
    return undefined;
  }
  try {
    const parsed = JSON.parse(value);
    return toJsonObject(parsed, label);
  } catch {
    throw new Error(`${label} must be valid JSON.`);
  }
}

function toJsonObject(value: unknown, label: string): JsonObject {
  if (!isJsonObject(value)) {
    throw new Error(`${label} must be a JSON object.`);
  }
  return value;
}

function isJsonObject(value: unknown): value is JsonObject {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  return Object.values(value).every(isJsonValue);
}

function isJsonValue(value: unknown): value is JsonValue {
  if (value === null) {
    return true;
  }
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return true;
  }
  if (Array.isArray(value)) {
    return value.every(isJsonValue);
  }
  return isJsonObject(value);
}

function stringifyJson(value: JsonObject | undefined): string {
  return JSON.stringify(value ?? {}, null, 2);
}

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

function formatUserOption(user: TenantDirectoryUserRecord): string {
  const parts = [user.displayName];
  if (user.email) {
    parts.push(user.email);
  }
  parts.push(user.id);
  return parts.join(" · ");
}

function formatProviderLinkTitle(
  record: ProviderIdentityLinkRecord,
  usersById: Map<string, TenantDirectoryUserRecord>,
): string {
  const user = usersById.get(record.userId);
  if (user) {
    return user.displayName;
  }
  return `User ${record.userId}`;
}
